import React, { useState } from 'react';
import {
  MapPinIcon,
  UserIcon,
  StarIcon,
  EyeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import MarketplaceAssetDetail from './MarketplaceAssetDetail';

const MarketplaceAssetCard = ({ asset }) => {
  const [showDetail, setShowDetail] = useState(false);
  const navigate = useNavigate();
  
  const getCategoryEmoji = (category) => {
    const emojis = {
      vehicle: '🚗',
      yacht: '⛵',
      property: '🏠',
      luxury: '💎',
      equipment: '🔧',
      other: '📦'
    };
    return emojis[category] || '📦';
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      vehicle: 'from-blue-600 to-blue-700',
      yacht: 'from-cyan-600 to-cyan-700',
      property: 'from-green-600 to-green-700',
      luxury: 'from-purple-600 to-purple-700',
      equipment: 'from-orange-600 to-orange-700',
      other: 'from-gray-600 to-gray-700'
    };
    return colors[category] || 'from-gray-600 to-gray-700';
  };
  
  return (
    <>
      <div 
        className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden hover:border-purple-500 transition-all cursor-pointer group"
        onClick={() => setShowDetail(true)}
      >
        {/* Image/Category Badge */}
        <div className={`h-48 bg-gradient-to-br ${getCategoryColor(asset.category)} flex items-center justify-center relative`}>
          <span className="text-8xl">{getCategoryEmoji(asset.category)}</span>
          
          {/* Category Badge */}
          <div className="absolute top-3 left-3 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-lg text-xs font-medium text-white">
            {asset.category.charAt(0).toUpperCase() + asset.category.slice(1)}
          </div>
          
          {/* Views Badge */}
          <div className="absolute top-3 right-3 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-lg text-xs font-medium text-white flex items-center space-x-1">
            <EyeIcon className="h-3 w-3" />
            <span>{asset.views_count || 0}</span>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Title */}
          <h3 className="text-lg font-bold text-white group-hover:text-purple-400 transition-colors line-clamp-2">
            {asset.title}
          </h3>
          
          {/* Description */}
          <p className="text-sm text-white line-clamp-2">
            {asset.description}
          </p>
          
          {/* Location */}
          <div className="flex items-center space-x-2 text-sm text-white">
            <MapPinIcon className="h-4 w-4" />
            <span>{asset.location}</span>
          </div>
          
          {/* Seller Info */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                {asset.seller_profile?.nickname ? (
                  <span className="text-white font-bold text-sm">
                    {asset.seller_profile.nickname.charAt(0).toUpperCase()}
                  </span>
                ) : (
                  <UserIcon className="h-4 w-4 text-white" />
                )}
              </div>
              
              <div className="flex flex-col">
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-medium text-white">
                    {asset.seller_profile?.nickname || asset.seller || 'Seller'}
                  </span>
                  {asset.seller_profile?.is_verified && (
                    <ShieldCheckIcon className="h-4 w-4 text-green-400" />
                  )}
                </div>
                
                {asset.seller_profile && (
                  <div className="flex items-center space-x-1">
                    <StarIcon className="h-3 w-3 text-white fill-current" />
                    <span className="text-xs text-white">
                      {asset.seller_profile.rating?.toFixed(1) || '0.0'} 
                      <span className="text-white ml-1">
                        ({asset.seller_profile.total_trades || 0} trades)
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Price Section */}
          <div className="pt-3 border-t border-gray-700">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-white">Price</p>
                <p className="text-2xl font-bold text-white">
                  {parseFloat(asset.price_btc).toFixed(4)} <span className="text-lg text-purple-400">BTC1</span>
                </p>
              </div>
              
              <div className="text-right">
                <p className="text-xs text-white">Value</p>
                <p className="text-sm font-medium text-white">
                  ${parseFloat(asset.valuation_usd).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          {/* View Button */}
          <button className="w-full py-3 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium group-hover:shadow-lg">
            View Details
          </button>
        </div>
      </div>
      
      {/* Detail Modal */}
      {showDetail && (
        <MarketplaceAssetDetail
          assetId={asset.asset_id}
          onClose={() => setShowDetail(false)}
        />
      )}
    </>
  );
};

export default MarketplaceAssetCard;

